import React, { useState } from "react";
import {
  bookingTypeEnumToTranslatedText,
  differenceBetweenDatesAsDaysInteger,
  parseFinnishDateString,
} from "../../common/misc";
import { BookingItem, BookingType } from "./bookingsSlice";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { dateLib } from "react-day-picker";
import { ICalEventModal } from "../../components/ICalEventModal";

const BookingRowsTable = ({
  bookings,
  accommodationId,
  clearAllPastBookings,
  showCottageName,
  onICalSaved,
}: {
  bookings: BookingItem[];
  accommodationId?: string;
  clearAllPastBookings: boolean;
  showCottageName?: boolean;
  onICalSaved?: () => void;
}) => {
  const [showICalModal, setShowICalModal] = useState(false);
  const [selectedICalBooking, setSelectedICalBooking] =
    useState<BookingItem | null>(null);
  const [selectedICalAccommodationId, setSelectedICalAccommodationId] =
    useState<string | undefined>(undefined);

  const handleICalClick = (booking: BookingItem) => {
    setSelectedICalBooking(booking);
    setSelectedICalAccommodationId(accommodationId ?? booking.accommodationId);
    setShowICalModal(true);
  };
  const renderAddons = (booking: BookingItem) => {
    if (booking.bookingsAddonItems != null) {
      return (
        <>
          {booking.bookingsAddonItems.map((addon, index) => (
            <React.Fragment key={`${booking.id}-addon-${index}`}>
              {index > 0 ? " " : ""}
              <span className="text-nowrap">
                {addon.units + " x " + addon.productName}
              </span>
            </React.Fragment>
          ))}
        </>
      );
    }
    return null;
  };

  function renderTextWithLineBreaks(text: string): JSX.Element[] {
    return text.split(/<br\s*\/?>/i).map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split(/<br\s*\/?>/i).length - 1 && <br />}
      </React.Fragment>
    ));
  }

  const renderICalBookingRow = (booking: BookingItem) => {
    const startDate = parseFinnishDateString(booking.startDateString);
    const endDate = parseFinnishDateString(booking.endDateString);
    const bookingNotes = booking.ownerBookingNotes;

    return (
      <React.Fragment key={booking.id}>
        <tr className="position-relative">
          <th scope="row">
            <div>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleICalClick(booking);
                }}
                className="text-decoration-underline"
              >
                iCal -tapahtuma
              </a>
            </div>
          </th>
          {showCottageName && (
            <td className="d-none d-md-table-cell">{booking.itemName}</td>
          )}
          <td>
            <span className="d-none d-md-inline">-</span>
          </td>
          <td className="d-none d-md-table-cell">
            {booking.startDateString} {booking.checkInTime || ""} -{" "}
            {booking.endDateString} {booking.checkOutTime || ""}
            <span className="d-block">
              {differenceBetweenDatesAsDaysInteger(startDate, endDate)} vrk
            </span>
          </td>
          <td className="d-none d-md-table-cell">
            {booking.adults} + {booking.children}
          </td>
          <td className="d-none d-md-table-cell"></td>
        </tr>
        {bookingNotes && (
          <tr>
            <td colSpan={5} className="py-0">
              <div className="mt-n4 bg-light-subtle rounded p-8 mb-8">
                <span className="d-block d-md-inline-block fw-medium">
                  Lisätiedot:
                </span>
                <span> {renderTextWithLineBreaks(bookingNotes)}</span>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderBookingRow = (
    booking: BookingItem,
    isExtraBooking = false,
    nextBookingIsExtraBooking = false,
  ) => {
    const startDate = parseFinnishDateString(booking.startDateString);
    const endDate = parseFinnishDateString(booking.endDateString);

    let bookingNotes = booking.customerBookingNotes;
    /*if (
      booking.bookingType === BookingType.TemporaryDisabled ||
      booking.bookingType === BookingType.OwnerOwn
    ) {*/
    if (bookingNotes) {
      bookingNotes += "<br /> ";
    }
    bookingNotes += booking.ownerBookingNotes;
    //}

    return (
      <React.Fragment key={booking.id}>
        <tr
          className={`position-relative ${booking.bookingsAddonItems?.length > 0 || bookingNotes || nextBookingIsExtraBooking ? "remove-bottom-border" : ""} ${isExtraBooking ? " bg-light " : ""} `}
        >
          <th scope="row" className={`${isExtraBooking ? " ps-24 " : ""}`}>
            {isExtraBooking ? (
              <div>
                {`* ${booking.bookingNumber}`}
                {showCottageName && (
                  <span className="fw-normal d-block d-md-none">
                    {booking.itemName}
                  </span>
                )}
                <span className="d-block fw-normal">Lisävaraus</span>
              </div>
            ) : (
              <Link
                to={
                  `/bookings/booking/${booking.id}?bookingNumber=${booking.bookingNumber}` +
                  (accommodationId ? `&accommodationId=${accommodationId}` : "")
                }
              >
                {booking.bookingNumber}
                {showCottageName && (
                  <span className="fw-normal d-block d-md-none">
                    {booking.itemName}
                  </span>
                )}
                <span className="d-block fw-normal">
                  {bookingTypeEnumToTranslatedText(booking.bookingType)}
                </span>
              </Link>
            )}
          </th>
          {showCottageName && (
            <td className="d-none d-md-table-cell">{booking.itemName}</td>
          )}
          <td>
            {booking.payerFirstName} {booking.payerLastName}
            <div className="d-block d-md-none">
              {booking.startDateString} - {booking.endDateString}
              <span className="d-block">
                {differenceBetweenDatesAsDaysInteger(startDate, endDate)} vrk
              </span>
              <span className="d-block">
                {booking.adults} + {booking.children} hlöt
              </span>
            </div>
          </td>

          <td className="d-none d-md-table-cell">
            {booking.startDateString} - {booking.endDateString}
            <span className="d-block">
              {differenceBetweenDatesAsDaysInteger(startDate, endDate)} vrk
            </span>
          </td>
          <td className="d-none d-md-table-cell">
            {booking.adults} + {booking.children}
          </td>
          <td className="d-none d-md-table-cell">{renderAddons(booking)}</td>
        </tr>
        {(booking.bookingsAddonItems?.length > 0 || bookingNotes) && (
          <tr
            className={`${isExtraBooking ? " bg-light " : ""} ${nextBookingIsExtraBooking ? " remove-bottom-border " : ""}`}
          >
            <td
              colSpan={showCottageName ? 6 : 5}
              className={`"py-0 " ${isExtraBooking ? " ps-24 " : ""}`}
            >
              {booking.bookingsAddonItems?.length > 0 && (
                <div className="d-md-none mt-n4 bg-light-subtle rounded p-8">
                  <span className="d-block fw-medium">Lisäpalvelut:</span>
                  <div>{renderAddons(booking)}</div>
                </div>
              )}

              {bookingNotes && (
                <div className="mt-n4 bg-light-subtle rounded p-8 mb-8">
                  <span className="d-block d-md-inline-block fw-medium">
                    Lisätiedot:
                  </span>
                  <span> {renderTextWithLineBreaks(bookingNotes)}</span>
                </div>
              )}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };
  if (bookings === undefined || bookings === null) {
    return (
      <>
        <div className="d-flex gap-16 align-items-center">
          <Spinner animation="border" />
          <span>Ladataan varauksia…</span>
        </div>
      </>
    );
  }

  return (
    <>
      <table className="table table-mobile overflow-auto mb-0 mt-16">
        <thead>
          <tr>
            <th scope="col">
              <span className="text-nowrap">
                Nro
                {showCottageName && (
                  <span className="d-inline-block d-md-none">, kohde</span>
                )}
                <span> & tyyppi</span>
              </span>
            </th>
            {showCottageName && (
              <th scope="col" className="d-none d-md-table-cell">
                Kohde
              </th>
            )}
            <th scope="col">
              Asiakas
              <span className="d-inline-block d-md-none"> , kesto & hlöt</span>
            </th>
            <th scope="col" className="d-none d-md-table-cell">
              Kesto
            </th>
            <th scope="col" className="d-none d-md-table-cell">
              Hlöt
            </th>
            <th scope="col" className="d-none d-md-table-cell">
              Lisäpalvelut
            </th>
          </tr>
        </thead>
        <tbody>
          {bookings
            .filter((b) => {
              const endDate = new Date(b.endDate);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return endDate >= today;
            })
            .flatMap((b) => {
              const isICalBooking =
                b.iCalExternalUid && b.iCalExternalUid.trim() !== "";

              // If it's an iCal booking, render it separately
              if (isICalBooking) {
                return [renderICalBookingRow(b)];
              }

              // Otherwise, render normal bookings
              let mainBookingHasExtraBookings = false;
              if (
                b.extraBookingResponses &&
                b.extraBookingResponses.length > 0
              ) {
                mainBookingHasExtraBookings = true;
              }
              // Always include the main booking
              const rows = [
                renderBookingRow(b, false, mainBookingHasExtraBookings),
              ];
              // Then add extra responses, if any
              if (
                b.extraBookingResponses &&
                b.extraBookingResponses.length > 0
              ) {
                for (const extra of b.extraBookingResponses) {
                  if (!extra.isCancelled) {
                    rows.push(renderBookingRow(extra, true));
                  } else {
                    console.log(
                      "Skipping cancelled extra booking: " +
                        extra.bookingNumber,
                    );
                  }
                }
              }
              return rows;
            })}
        </tbody>
      </table>

      <ICalEventModal
        show={showICalModal}
        onHide={() => setShowICalModal(false)}
        eventData={selectedICalBooking}
        accommodationId={selectedICalAccommodationId ?? accommodationId}
        onSave={onICalSaved}
      />
    </>
  );
};

export default BookingRowsTable;
